import { useMutation } from "@apollo/client";
import Upload from "@mui/icons-material/Upload";
import { Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import EncampAutocomplete from "components/Forms/EncampAutocomplete";
import {
  getWastePipelineConfig,
  isWastePipelineName,
  WASTE_PIPELINE_NAMES,
  WASTE_PIPELINE_REGISTRY,
  WastePipelineName,
} from "encamp-shared/src/pipelines";
import { gql } from "generated-graphql";
import { useCallback, useState } from "react";
import { uploadFileDirectly } from "util/uploadFile";

const DISPATCH_WASTE_PIPELINE_RUN_MUTATION = gql(`
  mutation DispatchWastePipelineRun($input: DispatchWastePipelineRunInput!) {
    dispatchWastePipelineRun(input: $input) {
      id
      status
      task
    }
  }
`);

const SEARCH_TENANTS_QUERY = gql(`
  query DispatchPipelineSearchTenants($search: String, $pageSize: Int, $sort: [SortModel!]) {
    tenants(search: $search, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
      }
      count
    }
  }
`);

type TenantOption = { id: string; name: string };

const PIPELINE_OPTIONS = WASTE_PIPELINE_NAMES.map((name) => ({
  label: WASTE_PIPELINE_REGISTRY[name].label,
  value: name,
}));

function getFixedTenant(name: WastePipelineName): TenantOption | null {
  return WASTE_PIPELINE_REGISTRY[name].fixedTenant;
}

interface DispatchWastePipelineRunDialogProps {
  onClose: () => void;
}

export function DispatchWastePipelineRunDialog({ onClose }: DispatchWastePipelineRunDialogProps) {
  const alerts = useAlerts();

  const [pipelineName, setPipelineName] = useState<WastePipelineName>("clean-harbors");
  const [selectedTenant, setSelectedTenant] = useState<TenantOption | null>(null);
  const [tenantSearch, setTenantSearch] = useState("");
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [submitting, setSubmitting] = useState(false);

  const [dispatchMutation] = useMutation(DISPATCH_WASTE_PIPELINE_RUN_MUTATION);

  const config = getWastePipelineConfig(pipelineName);
  const fixedTenant = getFixedTenant(pipelineName);
  const effectiveTenant = fixedTenant ?? selectedTenant;
  const allRequiredFilesSelected = config.files
    .filter((slot) => slot.required)
    .every((slot) => files[slot.key] != null);
  const atLeastOneOfRuleMet =
    !config.requireAtLeastOneOf || config.requireAtLeastOneOf.some((key) => files[key] != null);
  const canSubmit =
    pipelineName &&
    effectiveTenant &&
    allRequiredFilesSelected &&
    atLeastOneOfRuleMet &&
    !submitting;

  const handleSubmit = useCallback(async () => {
    if (!effectiveTenant) return;

    setSubmitting(true);
    try {
      const sourceFiles: { key: string; s3Key: string }[] = [];

      for (const slot of config.files) {
        const file = files[slot.key];
        if (file) {
          const s3Key = await uploadFileDirectly(file, {
            tenantId: effectiveTenant.id,
          });
          sourceFiles.push({ key: slot.key, s3Key });
        }
      }

      const { data } = await dispatchMutation({
        variables: {
          input: {
            pipelineName,
            tenantId: effectiveTenant.id,
            sourceFiles,
          },
        },
      });

      const jobId = data?.dispatchWastePipelineRun.id;
      alerts.success(
        jobId ? (
          <>
            Pipeline dispatched successfully.{" "}
            <a href={`/staff/jobs/${jobId}`} target="_blank" rel="noopener noreferrer">
              View job
            </a>
          </>
        ) : (
          "Pipeline dispatched successfully."
        )
      );
      onClose();
    } catch (err) {
      alerts.error(
        `Failed to dispatch pipeline: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setSubmitting(false);
    }
  }, [effectiveTenant, files, config.files, pipelineName, dispatchMutation, alerts, onClose]);

  const handleFileChange = (key: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0];
    if (selected) {
      setFiles((prev) => ({ ...prev, [key]: selected }));
    }
    // Reset the input so the same file can be re-selected
    event.target.value = "";
  };

  const handlePipelineChange = (value: WastePipelineName) => {
    setPipelineName(value);
    setFiles({});
    if (getFixedTenant(value)) {
      setSelectedTenant(null);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title="New Pipeline Run"
      primaryActionButtonProps={{
        onClick: handleSubmit,
        saveText: "Dispatch",
        loading: submitting,
        disabled: !canSubmit,
      }}
    >
      <Stack spacing={3} sx={{ pt: 1 }}>
        <TextField
          select
          label="Pipeline Type"
          value={pipelineName}
          onChange={(e) => {
            const val = e.target.value;
            if (isWastePipelineName(val)) {
              handlePipelineChange(val);
            }
          }}
          fullWidth
        >
          {PIPELINE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        {fixedTenant ? (
          <TextField label="Organization" value={fixedTenant.name} disabled fullWidth />
        ) : (
          <EncampAutocomplete
            query={SEARCH_TENANTS_QUERY}
            getItems={(data) => data?.tenants.items ?? []}
            getOptionLabel={(option) => option.name}
            variables={{
              search: tenantSearch,
              pageSize: 25,
              sort: [{ field: "name", sort: "asc" as const }],
            }}
            value={selectedTenant}
            onChange={(value) => setSelectedTenant(value)}
            onInputChange={(value) => setTenantSearch(value)}
            label="Organization"
          />
        )}

        {config.requireAtLeastOneOf && (
          <Typography variant="body2" color="text.secondary">
            Provide at least one of:{" "}
            {config.requireAtLeastOneOf
              .map((key) => config.files.find((f) => f.key === key)?.label ?? key)
              .join(" or ")}
            .
          </Typography>
        )}

        {config.files.map((slot) => (
          <Stack key={slot.key} direction="row" alignItems="center" spacing={2}>
            <Button variant="outlined" component="label" startIcon={<Upload />}>
              {slot.label}
              {slot.required ? " *" : ""}
              <input
                type="file"
                accept={slot.accept}
                onChange={(e) => handleFileChange(slot.key, e)}
                hidden
              />
            </Button>
            {files[slot.key] && (
              <Typography variant="body2" color="text.secondary">
                {files[slot.key]?.name}
              </Typography>
            )}
          </Stack>
        ))}
      </Stack>
    </Dialog>
  );
}
